/**
 * Rules for the support team's first-response target and weekly hours, both
 * stored under `appconfigs.aiSupport` and saved through `updateAppConfig`.
 *
 * Hours are one global GMT schedule (fixed UTC+00:00, no daylight saving):
 * starts inclusive, ends exclusive, `24:00` only as an end, no overlaps or
 * duplicates, at most 28 windows. Days with no window are closed.
 */

export const DEFAULT_TARGET_SECONDS = 86400;
export const MAX_TARGET_SECONDS = 2592000;
export const MAX_WINDOWS = 28;
export const TIMEZONE = 'GMT';

export const WEEKDAYS = [
  {value: 'MONDAY', label: 'Monday'},
  {value: 'TUESDAY', label: 'Tuesday'},
  {value: 'WEDNESDAY', label: 'Wednesday'},
  {value: 'THURSDAY', label: 'Thursday'},
  {value: 'FRIDAY', label: 'Friday'},
  {value: 'SATURDAY', label: 'Saturday'},
  {value: 'SUNDAY', label: 'Sunday'},
];

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

const minutesOf = time =>
  time === '24:00'
    ? 24 * 60
    : Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));

/** The stored target, or the backend's 24-hour default when unset/invalid. */
export const effectiveTarget = seconds =>
  Number.isInteger(seconds) && seconds >= 1 && seconds <= MAX_TARGET_SECONDS
    ? seconds
    : DEFAULT_TARGET_SECONDS;

/** Hours typed by the admin → whole seconds, or an error message. */
export const targetFromHours = hours => {
  const value = Number(hours);
  if (hours === null || hours === undefined || hours === '' || !(value > 0)) {
    return {error: 'Enter a target above zero.'};
  }
  const seconds = Math.round(value * 3600);
  if (seconds < 1 || seconds > MAX_TARGET_SECONDS) {
    return {error: 'Pick a target of 30 days (720 hours) or less.'};
  }
  return {seconds};
};

/** A stored schedule as editable rows, one list of windows per weekday. */
export const toDays = availability => {
  const days = Object.fromEntries(WEEKDAYS.map(day => [day.value, []]));
  (availability?.weeklyHours || []).forEach(window => {
    if (days[window.dayOfWeek]) {
      days[window.dayOfWeek].push({
        start: window.startTime,
        end: window.endTime,
      });
    }
  });
  return days;
};

/** Editable rows → the API's `weeklyHours`, Monday first. */
export const toWeeklyHours = days =>
  WEEKDAYS.flatMap(day =>
    (days[day.value] || []).map(window => ({
      dayOfWeek: day.value,
      startTime: window.start,
      endTime: window.end,
    })),
  );

/**
 * Everything wrong with a week, as `{MONDAY: ['…'], _all: ['…']}`. Empty
 * object means it can be saved.
 */
export const scheduleErrors = days => {
  const errors = {};
  const add = (key, message) => {
    errors[key] = [...(errors[key] || []), message];
  };
  let count = 0;
  WEEKDAYS.forEach(({value: day, label}) => {
    const windows = days[day] || [];
    count += windows.length;
    const valid = [];
    windows.forEach(({start, end}) => {
      if (!TIME.test(start || '')) {
        add(day, `${label}: start "${start || ''}" must be HH:mm, e.g. 08:00.`);
        return;
      }
      if (end !== '24:00' && !TIME.test(end || '')) {
        add(day, `${label}: end "${end || ''}" must be HH:mm or 24:00.`);
        return;
      }
      if (minutesOf(start) >= minutesOf(end)) {
        add(
          day,
          `${label}: ${start}–${end} must end after it starts. For an overnight shift, end at 24:00 and continue the next day.`,
        );
        return;
      }
      valid.push([minutesOf(start), minutesOf(end), `${start}–${end}`]);
    });
    valid
      .sort((a, b) => a[0] - b[0])
      .forEach((window, index, all) => {
        const next = all[index + 1];
        // Ends are exclusive, so 08:00–12:00 then 12:00–17:00 is fine.
        if (next && next[0] < window[1]) {
          add(day, `${label}: ${window[2]} overlaps ${next[2]}.`);
        }
      });
  });
  if (count > MAX_WINDOWS) {
    add('_all', `Use ${MAX_WINDOWS} time windows or fewer across the week.`);
  }
  return errors;
};

/** "Mon–Fri 08:00–17:00"-style lines for the current schedule. */
export const describeSchedule = availability => {
  if (!availability) return null;
  const days = toDays(availability);
  const open = WEEKDAYS.filter(day => days[day.value].length);
  if (!open.length) return ['Closed all week'];
  return open.map(
    day =>
      `${day.label}: ${days[day.value]
        .map(window => `${window.start}–${window.end}`)
        .join(', ')}`,
  );
};
